/**
 * Navigation Items Hook
 *
 * Returns filtered navigation items based on user permissions.
 */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@/core/auth/auth.store';
import type { PermissionType } from '@/core/auth/types';
import {
  MAIN_NAV_ITEMS,
  SECONDARY_NAV_ITEMS,
  BOTTOM_NAV_ITEMS,
  getAllNavItems,
} from '../config/navigation.config';
import type { NavigationItem } from '../types';

interface UseNavigationItemsReturn {
  /** Main navigation items (filtered by permission) */
  mainItems: NavigationItem[];
  /** Secondary navigation items (filtered by permission) */
  secondaryItems: NavigationItem[];
  /** Bottom nav items for mobile (filtered by permission) */
  bottomNavItems: NavigationItem[];
  /** All items as flat list */
  allItems: NavigationItem[];
  /** Current active item based on path */
  activeItem: NavigationItem | undefined;
  /** Check if a path is active */
  isActive: (path: string) => boolean;
}

/**
 * Hook to get navigation items filtered by user permissions
 */
export const useNavigationItems = (): UseNavigationItemsReturn => {
  const location = useLocation();
  const { hasPermission, user } = useAuthStore();
  const businessType = user?.businessType ?? null;

  /**
   * Filter items based on user permissions AND business type visibility.
   * An item is dropped if either:
   *   - it has a `permission` the user lacks, or
   *   - it has `hideForBusinessTypes` that includes the tenant's businessType.
   * Items without either field are always shown. A null businessType (backend
   * couldn't resolve it) is treated as "unknown" — the full nav is shown so
   * the user isn't accidentally locked out.
   */
  const filterByVisibility = (items: NavigationItem[]): NavigationItem[] => {
    return items.filter((item) => {
      if (item.permission && !hasPermission(item.permission as PermissionType)) {
        return false;
      }
      if (
        item.hideForBusinessTypes &&
        businessType &&
        item.hideForBusinessTypes.includes(businessType)
      ) {
        return false;
      }
      return true;
    });
  };

  /**
   * Check if a path is currently active
   */
  const isActive = (path: string): boolean => {
    if (path === '#more') return false;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const mainItems = useMemo(
    () => filterByVisibility(MAIN_NAV_ITEMS),
    [hasPermission, businessType]
  );

  const secondaryItems = useMemo(
    () => filterByVisibility(SECONDARY_NAV_ITEMS),
    [hasPermission, businessType]
  );

  const bottomNavItems = useMemo(
    () => filterByVisibility(BOTTOM_NAV_ITEMS),
    [hasPermission, businessType]
  );

  const allItems = useMemo(
    () => filterByVisibility(getAllNavItems()),
    [hasPermission, businessType]
  );

  const activeItem = useMemo(
    () => allItems.find((item) => isActive(item.path)),
    [allItems, location.pathname]
  );

  return {
    mainItems,
    secondaryItems,
    bottomNavItems,
    allItems,
    activeItem,
    isActive,
  };
};

export default useNavigationItems;
